import {ActionContext} from "vuex";
import {IAppState} from "@/interfaces/app-state.interface";
import {Vue} from "vue-property-decorator";
import {httpResource} from "@/resources/http.resource";
import {IReport} from "@/modules/Report/interfaces/report.interface";
import {IApp} from "@/interfaces/app.interface";
import {IAppTable} from "@/shared/AppTable/interfaces/sort-table-data";
import {IAgentReport} from "@/modules/Report/interfaces/agent-report.interface";
import {bus} from "@/plugins/bus";
import {IDialog} from "@/modules/Popup/interfaces/dialog.interface";
import {IMovementsReport} from "@/modules/Report/Movements/interfaces/movements-report.interface";

type ReportStore = ActionContext<IReport.IState, IAppState>;

export const ReportStore: ReportStore = {
  // @ts-ignore
  namespaced: true,

  state: {
    activeReport: null,
    report: [],
    previousReport: {
      name: null,
      list: [],
    },
    tableTotal: {
      limitCntRows: 1000,
      limitMessageFlag: false,
    },
    filters: {...IReport.DefaultFilters,},
    defaultFilters: {...IReport.DefaultFilters,},
    filtersLocal: {},
    loading: false,
    displayDetails: false,
    sortingTable: {
      prop: null,
      propType: IApp.SortingPropType.String,
      direction: IApp.SortDirection.Default,
    },
    reportPayload: null,
  },

  getters: {
    sortedList: (state: IReport.IState) => {
      return IAppTable.SortTableData({
        tableData: state.report,
        sortingTable: state.sortingTable,
        limitTotal: state.tableTotal.limitCntRows,
      });
    },
  },

  mutations: {
    setProperty(state: IReport.IState, { name, value }: { name: string, value: any}) { Vue.set(state, name, value); },
    setSubProperty(state: IReport.IState, { block, name, value }: { block: string, name: string, value: any}) { Vue.set(state[block], name, value); },
    startLoading(state: IReport.IState) { state.loading = true },
    stopLoading(state: IReport.IState) { state.loading = false },
    updateFilterProp(state: IReport.IState, { name, value }: { name: string, value: any}) { Vue.set(state.filters, name, value); },
    setSorting(state: IReport.IState, cell: IReport.ITableProp) {
      let sortingData = IAppTable.SetSorting(state.sortingTable, cell, state.activeReport);
      Vue.set(state, 'sortingTable', sortingData);
      IAppTable.ScrollTableToTop();
    },
  },

  actions: {
    async getReport(store: ReportStore, data: { reportName: IReport.ReportName, filters?: IReport.IFilters }) {
      store.commit('startLoading');
      let asyncAction = async () => {
        const action = IReport.Action;
        const params: any = data && data.filters ? IReport.PrepareFilters(data.filters) : {};
        params.command = data.reportName;

        const { data: { result: reportData }} = await httpResource.httpAction({ action, params });

        // Mapping table data.
        let arrayData = reportData.table;
        switch (data.reportName) {
          case IReport.ReportName.Agent:
            let usedGrouping = !!(data.filters && data.filters.groupTypes && data.filters.groupTypes.length);
            arrayData = IAgentReport.mappingTableData(arrayData, usedGrouping);
            store.commit('setProperty', { name: 'reportPayload', value: { summary: reportData.summary } });
            break;

          default:
            break;
        }

        store.commit('setProperty', { name: 'report', value: arrayData });
        store.commit('setProperty', { name: 'tableTotal', value: reportData.total });
        store.commit('setProperty', { name: 'activeReport', value: data.reportName });
        if (data.filters) store.commit('setProperty', { name: 'filters', value: data.filters });
      };

      await store.dispatch('defaultResourceAction', { asyncAction }, { root: true });
      store.commit('stopLoading');
    },

    async openChangeDialog(store: ReportStore, payload: { entity: IMovementsReport.IDataItem | IMovementsReport.ICarPopupTableItem; fromCarPopup: boolean; fromSelectMovePopup: boolean; }) {
      if ((payload.entity as any).subMovFlag) {
        await store.dispatch('dialog/openDialog', {
          blockName: 'reports',
          dialogName: 'selectMovementPopup',
          payload: {
            entity: payload.entity,
          },
        }, { root: true });
      }
      else {
        if (payload.fromSelectMovePopup) {
          await store.dispatch('dialog/closeDialog', { blockName: 'reports', dialogName: 'selectMovementPopup' }, { root: true });
        }

        let dialogPayload = payload.fromCarPopup
          ? {
            ...payload.entity,
            dateIn: (payload.entity as any).dateBeg,
            dateOut: (payload.entity as any).dateEnd,
            fromCarPopup: true,
            carId: store.rootState.car.activeCar?.carId,
          }
          : {...payload.entity};

        if (payload.fromCarPopup) store.commit('car/setProperty', { name: 'displayContent', value: false }, { root: true });

        await store.dispatch('dialog/openDialog', {
          blockName: 'reports',
          dialogName: 'reportMovementsChange',
          secondaryPopup: payload.fromCarPopup,
          payload: dialogPayload,
        }, { root: true });
      }
    },
  }
};
